import Card from 'components/card';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

const getColor = (margin: number): string => {
    if (margin == null) return '#6b7280';
    if (margin < 5) return '#d73027';
    if (margin < 15) return '#fc8d59';
    if (margin < 30) return '#fee08b';
    if (margin < 50) return '#91cf60';
    return '#1a9850';
};

function transformDataForScatterChart(allProductsMargin: any) {
    if (!allProductsMargin || !allProductsMargin.length) return [];
    const result = [
        {
            name: 'Products',
            data: allProductsMargin.map((product: any) => ({
                y: product.averagePurchasePrice,
                x: product.percentage,
                name: product.name,
                fillColor: getColor(product.percentage),
            })),
        },
    ];
    return result;
}

function Scatter(props: { title: string; data: any }) {
    const { title, data } = props;

    // Memory-based configuration: recalculated only when `data`
    // actually changes, and not on every parent render.
    const chart = useMemo(() => {
        if (!data || !data.length) return null;
        return {
            series: transformDataForScatterChart(data),
            options: {
                chart: {
                    height: 350,
                    type: 'scatter',
                    // Disable animations: the main cause of freezing
                    // of ApexCharts starting with just a few hundred SVG points.
                    animations: { enabled: false },
                    zoom: { enabled: true, type: 'xy' },
                },
                // Lightweight markers, without costly magnification on hover.
                markers: {
                    size: 4,
                    strokeWidth: 0,
                    hover: { sizeOffset: 1 },
                },
                dataLabels: { enabled: false },
                tooltip: {
                    intersect: true,
                    shared: false,
                    custom: function ({ seriesIndex, dataPointIndex, w }: any) {
                        const point =
                            w.config.series[seriesIndex].data[dataPointIndex];
                        return `<div class="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                            <p class="text-xs font-bold text-navy-700 dark:text-white">${
                                point.name ?? ''
                            }</p>
                            <p class="text-sm text-gray-500">Coût de revient: ${
                                point.y?.toFixed(2) ?? '-'
                            } €</p>
                            <p class="text-sm text-gray-500">Taux de marque: ${
                                point.x?.toFixed(2) ?? '-'
                            } %</p>
                        </div>`;
                    },
                },
                xaxis: {
                    tickAmount: 5,
                    labels: {
                        formatter: (val: any) => parseFloat(val).toFixed(2),
                    },
                    title: { text: 'Taux de marque (%)' },
                },
                yaxis: {
                    tickAmount: 1,
                    title: { text: 'Coût de revient (€)' },
                    labels: {
                        formatter: (val: any) => parseInt(val),
                    },
                },
            },
        };
    }, [data]);

    return (
        <Card extra={'p-5 text-dm lg:h-full max-h-[350px] border'}>
            <h4 className="ml-px text-base font-bold text-navy-700 dark:text-white">
                {title}
            </h4>
            {chart && (
                <div id="chart">
                    <ReactApexChart
                        options={chart.options as any}
                        series={chart.series}
                        type="scatter"
                        height={250}
                    />
                </div>
            )}
            {!data && (
                <div className="flex justify-center items-center h-96">
                    <p className="text-lg text-gray-500">Chargement...</p>
                </div>
            )}
            {data && data.length === 0 && (
                <div className="flex justify-center items-center h-96">
                    <p className="text-base text-gray-500">
                        Aucun produit avec prix d'achat et de vente indiqué
                    </p>
                </div>
            )}
            <div id="html-dist"></div>
        </Card>
    );
}

export default Scatter;
